/**
 * Liste des tags connus pour l'autocomplete lors de l'édition manuelle des tags.
 * Alignée sur les tags générés par tagGenerator (generateAutoTags) et formations (getFormationTag).
 */

import { CLASSICAL_FORMATIONS, HORIZONS_FORMATIONS, CLASSICAL_GENRES, getFormationTag, getGenreTag } from './formations'
import { getAllEraTagLabels } from './musicalEras'

const formationTags = [
  ...CLASSICAL_FORMATIONS.map(f => getFormationTag(f.id)).filter(Boolean),
  ...HORIZONS_FORMATIONS.map(f => getFormationTag(f.id)).filter(Boolean)
]
const formationTagsUnique = [...new Set(formationTags)]
const genreTags = CLASSICAL_GENRES.map(g => getGenreTag(g.id)).filter(Boolean)

export const KNOWN_TAGS = [
  // Fonctions
  'Tonique',
  'Dominante',
  'SousDominante',
  // Cadences
  'CadenceParfaite',
  'CadenceImparfaite',
  'CadencePlagale',
  'CadenceRompue',
  'DemiCadence',
  'Cadence6/4',
  // Renversements
  'PremierRenversement',
  'SecondRenversement',
  'SeptièmePremierRenversement',
  'SeptièmeSecondRenversement',
  'SeptièmeTroisièmeRenversement',
  // Extensions
  'Septième',
  'Neuvième',
  'Onzième',
  'Treizième',
  // Qualités
  'Diminué',
  'Augmenté',
  // Accords spéciaux
  'SixteAugmentée',
  'Allemande',
  'Française',
  'Italienne',
  'Napolitaine',
  'EmpruntModal',
  'EmpruntTonHomonyme',
  'EmpruntAutreMode',
  // Retards
  'Retard',
  'RetardTierce',
  // Progressions
  'ProgressionII-V-I',
  'ProgressionCircleOfFifths',
  // Contexte modal
  'Majeur',
  'Mineur',
  // Styles (périodes)
  'Baroque',
  'Classique',
  'Romantique',
  'Moderne',
  // Degrés
  'DegréI',
  'DegréII',
  'DegréIII',
  'DegréIV',
  'DegréV',
  'DegréVI',
  'DegréVII',
  // Décennies (Horizons)
  '1960s', '1970s', '1980s', '1990s', '2000s', '2010s', '2020s',
  // Horizons
  'Horizons', 'Film', 'JeuVideo', 'Anime', 'Variété', 'Pop',
  // Ères musicales (Horizons)
  ...getAllEraTagLabels(),
  // Formations (instrumentation) et Genres (type d'œuvre)
  ...formationTagsUnique,
  ...genreTags
]

/**
 * Sous-ensemble de tags toujours proposés dans les filtres (mode libre, dashboard prof),
 * même si aucun exercice ne les possède encore. Permet de filtrer par "Demi-Cadence", "Cadence parfaite", etc.
 */
export const CANONICAL_FILTER_TAGS = [
  'Tonique', 'Dominante', 'SousDominante',
  'CadenceParfaite', 'CadenceImparfaite', 'CadencePlagale', 'CadenceRompue', 'DemiCadence', 'Cadence6/4',
  'PremierRenversement', 'SecondRenversement', 'SeptièmePremierRenversement', 'SeptièmeSecondRenversement', 'SeptièmeTroisièmeRenversement',
  'Septième', 'Neuvième', 'Onzième', 'Treizième',
  'Diminué', 'Augmenté',
  'SixteAugmentée', 'Allemande', 'Française', 'Italienne', 'Napolitaine', 'EmpruntModal',
  'EmpruntTonHomonyme', 'EmpruntAutreMode',
  'Retard', 'RetardTierce',
  'ProgressionII-V-I', 'ProgressionCircleOfFifths',
  'Majeur', 'Mineur',
  'Baroque', 'Classique', 'Romantique', 'Moderne',
  'DegréI', 'DegréII', 'DegréIII', 'DegréIV', 'DegréV', 'DegréVI', 'DegréVII'
]

/**
 * Filtre les tags connus selon une requête (pour autocomplete).
 * @param {string} query - Texte saisi
 * @returns {string[]} - Tags correspondants (max 10)
 */
export function filterKnownTags(query) {
  if (!query || typeof query !== 'string') return KNOWN_TAGS.slice(0, 10)
  const q = query.trim().toLowerCase()
  if (!q) return KNOWN_TAGS.slice(0, 10)
  return KNOWN_TAGS.filter(tag => tag.toLowerCase().includes(q)).slice(0, 10)
}

const OBSOLETE_TAG_PATTERNS = [
  /^marqueurs[_:]?\d+$/i,
  /^structure[_]?(complexe|moyenne)$/i,
  /^multiples[_]?cadences$/i,
  /^renversements[_]?multiples$/i,
  /^compositeur[_]/i,
  /^moyen$/i,
  /^progression[_]?(chromatic|chromatique|descendante|ascendante)$/i,
]

/**
 * Retire les tags obsolètes (anciens tags stockés en base mais plus générés).
 * @param {string[]} tags
 * @returns {string[]}
 */
export function sanitizeAutoTags(tags) {
  if (!Array.isArray(tags)) return []
  return tags.filter(tag => {
    if (!tag || typeof tag !== 'string') return false
    return !OBSOLETE_TAG_PATTERNS.some(pattern => pattern.test(tag))
  })
}
